import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { MarketAccessory, MarketList, MarketLink } from '@market/react';
import Info from '@square-icons/react/16/UI/circle-info-fill';
import Ct2LearnMore from './ct2_learn_more';
import { displayVAT, toCurrency } from 'utilities/currency';
import { getLocalePath } from 'utilities/locales';
import ProductActionCard from 'components/pages/product/product_action_card';

const ProductAlternativeSet = (props) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const {
    currentProduct,
    alternatives,
    category,
    onAlternativeSelected,
    onComparisonTriggerClick,
    enableAlternativesModal = false,
  } = props;

  if (alternatives.length === 0) {
    return null;
  }

  const renderIdentifyLink = () => {
    const linkKey = `relations.alternative.${currentProduct.base_product_key}.identifyLink`;

    return i18n.exists(linkKey) ? (
      <p
        dangerouslySetInnerHTML={{
          __html: t(linkKey, { locale: getLocalePath() }),
        }}
        className="paragraph-20"
        style={{ marginTop: theme.spacing(1) }}
      />
    ) : null;
  };

  const getAltIcon = (alternative) => {
    if (category === 'connected_terminal') return null;

    let altIcon;
    try {
      // eslint-disable-next-line import/no-dynamic-require
      const icon = require(
        `images/product/alt_icons/${alternative.base_product_key}.svg`,
      );
      altIcon = icon.ReactComponent();
    } catch {
      altIcon = null;
    }
    return altIcon;
  };

  const labelText = (alternative) => {
    // Using enableAlternativesModal to control price display assumes these features
    // will always be coupled, which may not hold true in the future. Using this vs
    // creating another thing to feature flag since this is likely only temporary.
    if (enableAlternativesModal) {
      const formattedPrice = alternative.sale_price;
      const strikethrough = alternative.display_strikethrough;

      return (
        <>
          {strikethrough && (
            <s style={{ marginRight: theme.spacing(0.5) }}>
              {alternative.default_price}
            </s>
          )}
          {formattedPrice}
          {displayVAT() && (
            <span style={{ fontSize: theme.type.fontSize.tiny }}>
              {` ${t('common.plusVAT')}`}
            </span>
          )}
        </>
      );
    }

    if (
      category !== 'connected_terminal' ||
      alternative.base_product_key !== 'bundle-s1e-connected-terminal'
    ) {
      return null;
    }

    const minAltPrice = Math.min(
      ...alternatives.map((a) => a.sale_price_unformatted),
    );
    const price = alternative.sale_price_unformatted - minAltPrice;
    if (price <= 0) return null;
    const formattedPrice = toCurrency(price);

    return displayVAT() ? (
      <>
        {`+${formattedPrice} `}
        <span style={{ fontSize: theme.type.fontSize.tiny }}>
          {t('common.plusVAT')}
        </span>
      </>
    ) : (
      `+${formattedPrice}`
    );
  };

  const renderComparisonLink = () => {
    if (!enableAlternativesModal) return null;

    return (
      <MarketLink
        onClick={onComparisonTriggerClick}
        className="medium-10"
        style={{ marginLeft: theme.spacing(1), verticalAlign: 'middle' }}
      >
        <span>
          {t([
            `productPage.comparisonModal.${category}.title`,
            'productPage.comparisonModal.default.title',
          ])}
          <Info
            className="monochrome-override-info-icon"
            style={{
              fill: theme.color.blue,
              marginLeft: theme.spacing(0.5),
              verticalAlign: 'middle',
            }}
          />
        </span>
      </MarketLink>
    );
  };

  const altRow = (alternative) => {
    const altIcon = getAltIcon(alternative);

    const label = t([
      `relations.alternative.${alternative.base_product_key}.${category}.label`,
      `relations.alternative.${alternative.base_product_key}.label`,
    ]);

    const subtext = t(
      [
        `relations.alternative.${alternative.base_product_key}.${category}.subtext`,
        `relations.alternative.${alternative.base_product_key}.subtext`,
      ],
      { defaultValue: null },
    );

    return (
      <ProductActionCard
        key={alternative.slug}
        value={alternative.slug}
        label={label}
        subtext={subtext}
        secondaryLabel={labelText(alternative)}
        accessory={
          Boolean(altIcon) && (
            <MarketAccessory
              slot="leading-accessory"
              style={{ width: '50px', height: '50px' }}
            >
              {altIcon}
            </MarketAccessory>
          )
        }
      />
    );
  };

  const altRows = alternatives
    .sort((p1, _p2) => {
      // Put s1e first, then ct2
      if (category === 'connected_terminal') {
        return p1.base_product_key === 's1e' ? -1 : 1;
      }
      return 0;
    })
    .map((alternative) => altRow(alternative));

  return (
    <div>
      <p
        className="medium-30"
        style={{ margin: theme.spacing(2, 0), display: 'inline' }}
      >
        {t([
          `relations.alternative.${currentProduct.base_product_key}.${category}.title`,
          `relations.alternative.${currentProduct.base_product_key}.title`,
        ])}
      </p>
      {renderComparisonLink()}
      <MarketList
        interactive
        value={currentProduct.slug}
        onMarketListSelectionsDidChange={(e) =>
          onAlternativeSelected(e.detail.newSelectionValue)
        }
        style={{ margin: theme.spacing(2, 0) }}
      >
        {altRows}
      </MarketList>
      {category !== 'connected_terminal' && renderIdentifyLink()}
      {category === 'connected_terminal' && <Ct2LearnMore />}
    </div>
  );
};

const ProductAlternatives = (props) => {
  const {
    currentProduct,
    alternatives,
    currentAltMetadata,
    onAlternativeSelected,
    onComparisonTriggerClick,
    enableAlternativesModal = false,
  } = props;

  const byCategory = alternatives.reduce((acc, alt) => {
    if (alt.product_key === currentProduct.product_key) return acc;

    const metadata = currentAltMetadata[alt.product_key] || {};
    const category = metadata.category || 'default';
    // eslint-disable-next-line no-param-reassign
    acc[category] ||= [currentProduct];
    if (!metadata.hidden) {
      acc[category].push(alt);
    }

    return acc;
  }, {});

  return Object.entries(byCategory).map(([category, items]) => {
    const sortedAlternatives = items.sort((alt1, alt2) => {
      const rank1 =
        currentAltMetadata[alt1.product_key]?.rank ||
        alternatives.map((obj) => obj.product_key).indexOf(alt1.product_key);
      const rank2 =
        currentAltMetadata[alt2.product_key]?.rank ||
        alternatives.map((obj) => obj.product_key).indexOf(alt2.product_key);

      return rank2 - rank1;
    });

    const key = sortedAlternatives
      .map((product) => product.product_key)
      .join('-');

    return (
      <ProductAlternativeSet
        key={key}
        currentProduct={currentProduct}
        alternatives={sortedAlternatives}
        category={category}
        onAlternativeSelected={onAlternativeSelected}
        onComparisonTriggerClick={onComparisonTriggerClick}
        enableAlternativesModal={enableAlternativesModal}
      />
    );
  });
};

export default ProductAlternatives;
